import type {
  MedicationPadAlertError,
  MedicationPadItemType,
} from '@/components/Patient/components/PrescriptionPad/PrescriptionPad.types';
import type { FieldValidationResult } from '@/components/Patient/utils';
import { padItemSupportsMaxDailyDose } from '@/utils/dosageLimit/supportsMaxDailyDose';
import { ALERT_ACTION_REQUIRED_TITLE } from '../constants';
import type { PrescriptionPadRules } from '../types';

export const MAX_DAILY_DOSE_REQUIRED_ERROR_MESSAGE = 'Please provide a maximum daily dose.';
export const MAX_DAILY_DOSE_NOT_A_NUMBER_ERROR_MESSAGE = 'Maximum daily dose must be a number.';
export const MAX_DAILY_DOSE_NOT_POSITIVE_ERROR_MESSAGE = 'Maximum daily dose must be greater than 0.';

/**
 * A maximum daily dose is optional unless the pad rules require one, and once entered it must be a number greater
 * than 0. `NaN` is how the pad item records input that could not be read as a number.
 */
export const validateMaxDailyDose = (
  maxDailyDose: number | undefined,
  { requireMaxDailyDose }: PrescriptionPadRules,
): FieldValidationResult => {
  if (maxDailyDose === undefined) {
    return requireMaxDailyDose
      ? { hasError: true, errorMessage: MAX_DAILY_DOSE_REQUIRED_ERROR_MESSAGE }
      : { hasError: false };
  }

  if (!Number.isFinite(maxDailyDose)) {
    return { hasError: true, errorMessage: MAX_DAILY_DOSE_NOT_A_NUMBER_ERROR_MESSAGE };
  }

  if (maxDailyDose <= 0) {
    return { hasError: true, errorMessage: MAX_DAILY_DOSE_NOT_POSITIVE_ERROR_MESSAGE };
  }

  return { hasError: false };
};

export const getMaxDailyDoseError = (maxDailyDose: number | undefined, rules: PrescriptionPadRules): string => {
  const { hasError, errorMessage } = validateMaxDailyDose(maxDailyDose, rules);
  return hasError && errorMessage ? errorMessage : '';
};

/**
 * Nothing is reported with dispense limits off, where the input is hidden, until a product is selected (matching the
 * dosage alert), or for a formulation without a maximum daily dose.
 */
export const getMaxDailyDoseAlertError = (
  item: MedicationPadItemType,
  rules: PrescriptionPadRules,
): MedicationPadAlertError => {
  const { medicationPadItem } = item;
  if (!rules.enableDispenseLimits || medicationPadItem?.productId === undefined || !padItemSupportsMaxDailyDose(item)) {
    return null;
  }
  const errorMessage = getMaxDailyDoseError(medicationPadItem.maxDailyDose, rules);
  return errorMessage ? { title: ALERT_ACTION_REQUIRED_TITLE, description: errorMessage } : null;
};
